// =========================================
// TATTVA ANIMATION CONFIGURATION
// Centralized animation system for consistency
// =========================================

// Easing curves - GPU-optimized
export const easings = {
  // Primary easing for entrances
  easeOut: [0.16, 1, 0.3, 1],
  easeOutQuart: [0.25, 1, 0.5, 1],
  easeOutExpo: [0.19, 1, 0.22, 1],
  
  // For interactions
  easeInOut: [0.4, 0, 0.2, 1],
  spring: { type: 'spring', stiffness: 300, damping: 30 },
  springBounce: { type: 'spring', stiffness: 400, damping: 25 },
  
  // For exits
  easeIn: [0.4, 0, 1, 1],
};

// Duration system (in seconds)
export const durations = {
  instant: 0.1,
  fast: 0.2,
  normal: 0.4,
  slow: 0.6,
  slower: 0.8,
  slowest: 1.2,
};

// Stagger delays
export const staggers = {
  fast: 0.03,
  normal: 0.05,
  slow: 0.08,
  slower: 0.12,
};

// Y offset values for animations
export const offsets = {
  small: 12,
  medium: 20,
  large: 40,
  xlarge: 60,
};

// =========================================
// FRAMER MOTION VARIANTS
// =========================================

// Hero text entrance - word by word
export const heroTextVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggers.normal,
      delayChildren: 0.1,
    },
  },
};

export const heroWordVariants = {
  hidden: { 
    opacity: 0, 
    y: offsets.medium,
    filter: 'blur(4px)',
  },
  visible: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: durations.slow,
      ease: easings.easeOut,
    },
  },
};

// Character stagger (for special headings)
export const charStaggerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggers.fast,
    },
  },
};

export const charVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: durations.normal,
      ease: easings.easeOut,
    },
  },
};

// Section reveal
export const sectionRevealVariants = {
  hidden: { 
    opacity: 0, 
    y: offsets.large,
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: durations.slow,
      ease: easings.easeOut,
    },
  },
};

// Staggered container for cards
export const staggerContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: staggers.slow,
      delayChildren: 0.1,
    },
  },
};

// Card reveal variant
export const cardRevealVariants = {
  hidden: { 
    opacity: 0, 
    y: offsets.medium,
    scale: 0.95,
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: durations.slow,
      ease: easings.easeOut,
    },
  },
};

// Hover effects for cards
export const cardHoverVariants = {
  rest: { 
    scale: 1,
    y: 0,
    boxShadow: '0 0 0 rgba(0,0,0,0)',
  },
  hover: { 
    scale: 1.03,
    y: -8,
    boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
    transition: {
      duration: durations.fast,
      ease: easings.easeOut,
    },
  },
  tap: {
    scale: 0.97,
    transition: {
      duration: durations.instant,
    },
  },
};

// Button hover/tap
export const buttonVariants = {
  rest: { 
    scale: 1,
  },
  hover: { 
    scale: 1.05,
    transition: {
      duration: durations.fast,
      ease: easings.easeOut,
    },
  },
  tap: {
    scale: 0.95,
    transition: {
      duration: durations.instant,
    },
  },
};

// Floating element animation
export const floatVariants = {
  initial: { y: 0 },
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

// Parallax element
export const parallaxVariants = {
  initial: { y: 0 },
  animate: (speed = 0.5) => ({
    y: speed * 100,
    transition: {
      duration: 0,
    },
  }),
};

// Fade up on scroll
export const fadeUpVariants = {
  hidden: { 
    opacity: 0, 
    y: offsets.small,
  },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: durations.normal,
      ease: easings.easeOut,
    },
  },
};

// Scale in variant
export const scaleInVariants = {
  hidden: { 
    opacity: 0, 
    scale: 0.8,
  },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: {
      duration: durations.slow,
      ease: easings.easeOutQuart,
    },
  },
};

// =========================================
// GSAP CONFIGURATION
// =========================================

export const gsapDefaults = {
  // Default ScrollTrigger settings
  scrollTrigger: {
    start: 'top 80%',
    end: 'bottom 20%',
    toggleActions: 'play none none none',
  },
  
  // Performance-optimized properties
  // ONLY animate transform and opacity for GPU acceleration
  fadeUp: {
    from: { opacity: 0, y: 40 },
    to: { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' },
  },
  
  scaleIn: {
    from: { opacity: 0, scale: 0.9 },
    to: { opacity: 1, scale: 1, duration: 0.6, ease: 'power3.out' },
  },
  
  slideIn: {
    from: { opacity: 0, x: -40 },
    to: { opacity: 1, x: 0, duration: 0.6, ease: 'power3.out' },
  },
};

// =========================================
// REDUCED MOTION SUPPORT
// =========================================

export const getReducedMotionVariants = (originalVariants) => {
  // Check if user prefers reduced motion
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return {
      hidden: { opacity: 0 },
      visible: { 
        opacity: 1,
        transition: { duration: 0.01 },
      },
    };
  }
  return originalVariants;
};

// Mobile optimized variants (shorter durations, smaller movements)
export const getMobileVariants = (originalVariants, isMobile) => {
  if (!isMobile) return originalVariants;
  
  return {
    ...originalVariants,
    visible: {
      ...originalVariants.visible,
      transition: {
        ...originalVariants.visible?.transition,
        duration: (originalVariants.visible?.transition?.duration || 0.6) * 0.6,
      },
    },
  };
};

// =========================================
// CSS CUSTOM PROPERTIES FOR ANIMATIONS
// =========================================

export const cssAnimationVars = `
  --animation-duration-instant: 0.1s;
  --animation-duration-fast: 0.2s;
  --animation-duration-normal: 0.4s;
  --animation-duration-slow: 0.6s;
  --animation-duration-slower: 0.8s;
  
  --animation-ease-out: cubic-bezier(0.16, 1, 0.3, 1);
  --animation-ease-in-out: cubic-bezier(0.4, 0, 0.2, 1);
  --animation-ease-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
`;

export default {
  easings,
  durations,
  staggers,
  offsets,
  heroTextVariants,
  heroWordVariants,
  charStaggerVariants,
  charVariants,
  sectionRevealVariants,
  staggerContainerVariants,
  cardRevealVariants,
  cardHoverVariants,
  buttonVariants,
  floatVariants,
  fadeUpVariants,
  scaleInVariants,
  gsapDefaults,
  getReducedMotionVariants,
  getMobileVariants,
};
